import React, { useState, useEffect } from 'react';
import { Trash2, Edit, X, Save, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../utils/config';
import { Link } from 'react-router-dom';

const CommentsAdmin = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('blog');
  const [editingComment, setEditingComment] = useState(null);
  const [editForm, setEditForm] = useState({ comment: '' });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  // Fetch comments based on active tab and pagination
  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        const { page, limit } = pagination;
        const offset = (page - 1) * limit;
        
        const endpoint = `${config.url}/admin/${activeTab}/comments?offset=${offset}&limit=${limit}`;
        
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          window.location.href = '/admin/login';
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch comments');
        }

        const data = await response.json();
        setComments(data.comments || []);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0
        }));
        
      } catch (error) {
        console.error('Error fetching comments:', error);
        toast.error(`Failed to load comments: ${error.message}`);
        setComments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [activeTab, pagination.page, pagination.limit]);

  // Handle comment deletion
  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      const endpoint = `${config.url}/admin/${activeTab}/comments/${commentId}`;
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }

      setComments(comments.filter(comment => comment.id !== commentId));
      toast.success('Comment deleted successfully');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error(`Failed to delete comment: ${error.message}`);
    }
  };

  // Handle edit form changes
  const handleEditChange = (e) => {
    setEditForm({
      ...editForm,
      [e.target.name]: e.target.value
    });
  };

  // Handle edit submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const endpoint = `${config.url}/admin/${activeTab}/comments/${editingComment.id}`;
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to update comment');
      }

      const updatedComment = await response.json();
      
      setComments(comments.map(comment => 
        comment.id === editingComment.id ? updatedComment : comment
      ));
      
      setEditingComment(null);
      toast.success('Comment updated successfully');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error(`Failed to update comment: ${error.message}`);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Calculate pagination
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const startItem = (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(startItem + pagination.limit - 1, pagination.total);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold">Comments Management</h1>
        <div className="flex space-x-2 bg-white p-1 rounded-lg border">
          <button
            onClick={() => setActiveTab('blog')}
            className={`px-4 py-2 rounded-md text-sm ${
              activeTab === 'blog' ? 'bg-blue-100 text-blue-700' : 'text-gray-600'
            }`}
          >
            Blog
          </button>
          <button
            onClick={() => setActiveTab('rachnaye')}
            className={`px-4 py-2 rounded-md text-sm ${
              activeTab === 'rachnaye' ? 'bg-blue-100 text-blue-700' : 'text-gray-600'
            }`}
          >
            Rachnaye
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : comments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No comments found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {activeTab === 'blog' ? 'Blog Post' : 'Rachnaye'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Comment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Author
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {comments.map((comment) => (
                  <tr key={comment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/${activeTab}/${comment[`${activeTab}_id`]}`}
                        target="_blank"
                        className="text-blue-600 hover:underline flex items-center"
                      >
                        {comment[`${activeTab}_title`] || 'N/A'}
                        <ExternalLink className="ml-1 h-3 w-3" />
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      {editingComment?.id === comment.id ? (
                        <input
                          type="text"
                          name="comment"
                          value={editForm.comment}
                          onChange={handleEditChange}
                          className="w-full border rounded px-2 py-1"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">{comment.comment}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium">{comment.user_name}</div>
                      <div className="text-xs text-gray-500">{comment.user_email || 'No email'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(comment.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {editingComment?.id === comment.id ? (
                          <>
                            <button
                              onClick={() => setEditingComment(null)}
                              className="text-gray-500 hover:text-gray-700"
                              title="Cancel"
                            >
                              <X className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleEditSubmit}
                              className="text-blue-600 hover:text-blue-900"
                              title="Save"
                            >
                              <Save className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingComment(comment);
                                setEditForm({ comment: comment.comment });
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(comment.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-700">
              Showing {startItem} to {endItem} of {pagination.total} results
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentsAdmin;
